import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getSiteRankings, getCannibalization, getPageDiagnosis, getTopics } from '../apiRankings';
import { getTimeline } from '../apiTimeline';
import AnnotatedTrend from './AnnotatedTrend';
import { useDomain } from '../DomainContext';
import PageHeader from '../ui/PageHeader';
import EmptyState from '../ui/EmptyState';
import Alert from '../ui/Alert';
import Spinner from '../ui/Spinner';
import Badge from '../ui/Badge';
import { formatCount } from '../pageMetrics';
import {
  POSITION_FILTERS, filterByPosition, matchesText, sortRows, formatPos, formatUsd, pathOf, slugOf, ACTIONS,
} from './rankings';
import {
  Dash, Kpi, SortTh, PosDelta, PctDelta, PositionTrend, TopicMap, Sparkline, TrendCell, trendLabel,
} from './parts';
import SiteSectionNav from '../ui/SiteSectionNav';

const DAY_OPTIONS = [7, 28, 90];
const SECTIONS = [
  { key: 'queries', label: 'Queries' },
  { key: 'pages', label: 'Pages' },
  { key: 'tracked', label: 'Tracked (1–20)' },
  { key: 'opportunities', label: 'Opportunities' },
  { key: 'lowctr', label: 'Low CTR' },
  { key: 'cannibal', label: 'Cannibalization' },
  { key: 'diagnosis', label: 'Page diagnosis' },
  { key: 'topics', label: 'Topics' },
];

const Chips = ({ options, value, onChange, label }) => (
  <div className="chip-group" role="group" aria-label={label}>
    {options.map((o) => (
      <button key={o.key} type="button" className={`chip${value === o.key ? ' active' : ''}`}
              aria-pressed={value === o.key} onClick={() => onChange(o.key)}>
        {o.label}{o.count != null ? <span className="count">{o.count}</span> : null}
      </button>
    ))}
  </div>
);

/* Filter bar shared by the query and page tables: text, position band, top N. */
const Filters = ({ text, setText, pos, setPos, n, setN, placeholder }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
    <input className="input" style={{ maxWidth: 260 }} placeholder={placeholder} value={text}
           onChange={(e) => setText(e.target.value)} aria-label={placeholder} />
    <Chips label="Position" options={POSITION_FILTERS} value={pos} onChange={setPos} />
    {setN && (
      <select className="select" value={n} onChange={(e) => setN(Number(e.target.value))} aria-label="How many">
        {[20, 50, 100].map((v) => <option key={v} value={v}>Top {v}</option>)}
      </select>
    )}
  </div>
);

const Card = ({ title, sub, children }) => (
  <div className="card">
    <div className="card-header"><h2 className="card-title">{title}</h2></div>
    {sub ? <p className="muted small" style={{ marginTop: 0 }}>{sub}</p> : null}
    {children}
  </div>
);

/* Lazily loaded section (cannibalization, diagnosis, topics are slower). */
const useLazy = (active, loader) => {
  const [state, setState] = useState({ data: null, loading: false });
  useEffect(() => {
    if (!active || state.data || state.loading) return;
    setState({ data: null, loading: true });
    loader().then((data) => setState({ data, loading: false }));
  }, [active, loader, state.data, state.loading]);
  return state;
};

const DomainRankings = () => {
  const { domainId } = useParams();
  const [params, setParams] = useSearchParams();
  const section = params.get('tab') || 'queries';
  const setSection = (k) => setParams((p) => { const q = new URLSearchParams(p); q.set('tab', k); return q; }, { replace: true });
  const { domains, selectedDomain } = useDomain();
  const domain = selectedDomain?.id === domainId ? selectedDomain : domains.find((d) => d.id === domainId) || null;
  const hostname = domain?.hostname || domain?.name || domainId.replace(/_/g, '.');

  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [pos, setPos] = useState('all');
  const [n, setN] = useState(20);
  const [sort, setSort] = useState('clicks');
  const [selCluster, setSelCluster] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setData(await getSiteRankings(domainId, days, 100));
    setLoading(false);
  }, [domainId, days]);
  useEffect(() => { load(); }, [load]);

  /* Timeline changes and notes, drawn as flags on the trend chart; a failure only hides the flags. */
  const [timeline, setTimeline] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getTimeline(domainId).then((d) => { if (!cancelled) setTimeline(d.entries || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [domainId]);

  const cannibal = useLazy(section === 'cannibal', useCallback(() => getCannibalization(domainId), [domainId]));
  const diagnosis = useLazy(section === 'diagnosis', useCallback(() => getPageDiagnosis(domainId), [domainId]));
  const topics = useLazy(section === 'topics', useCallback(() => getTopics(domainId), [domainId]));

  const editLink = (url) => {
    const slug = slugOf(url);
    return slug ? `/domain/${domainId}/pages/${encodeURIComponent(slug)}/edit` : null;
  };
  const PageCell = ({ url }) => (
    <td style={{ maxWidth: 340, wordBreak: 'break-all' }}>
      <a href={url} target="_blank" rel="noopener noreferrer" className="mono" style={{ fontSize: 12 }}>{pathOf(url)}</a>
      {editLink(url) && <> · <Link className="small" to={editLink(url)}>edit</Link></>}
    </td>
  );

  const view = (rows, field) => sortRows(filterByPosition(rows, pos).filter((r) => matchesText(r, text, field)), sort);
  const t = (data && data.totals) || {};
  const u = (data && data.untapped) || {};
  const m = (data && data.model) || {};
  const hasData = data && !data.error && (t.impressions || 0) > 0;
  const kpiSpark = (values, metric, invert = false) => (
    <Sparkline values={values} label={trendLabel(metric, days, invert)} invert={invert} width={120} height={24} />
  );

  const counts = data && !data.error ? {
    tracked: data.tracked.length, opportunities: data.opportunities.length, lowctr: data.low_ctr.length,
  } : {};

  return (
    <div className="page">
      <PageHeader
        title="Rankings"
        subtitle={`${hostname}: Google search positions, queries and pages (Search Console)`}
        actions={(
          <>
            <label className="sr-only" htmlFor="rank-days">Period</label>
            <select id="rank-days" className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {DAY_OPTIONS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
            <Link className="btn btn-secondary" to="/rankings">All sites</Link>
          </>
        )}
      />
      <SiteSectionNav section="growth" />

      {data && data.error && (
        <Alert type="error">{data.status === 401 ? 'Sign in again to load rankings.' : 'Could not load Search Console data.'}</Alert>
      )}

      {loading && !data ? <Spinner label="Loading rankings…" /> : !hasData && data && !data.error ? (
        <EmptyState title="No Search Console data yet"
                    message={`Nothing for ${hostname} in the last ${days} days. Either the property isn't verified for the console's Google account, or Google hasn't shown the site yet.`} />
      ) : hasData ? (
        <>
          <div className="kpi-grid">
            <Kpi label="Clicks" value={formatCount(t.clicks)} sub={<PctDelta cur={t.clicks} prev={t.prev_clicks} />}
                 hint={`vs the ${days} days before`} spark={kpiSpark(t.trend?.clicks, 'Clicks')} />
            <Kpi label="Impressions" value={formatCount(t.impressions)} sub={<PctDelta cur={t.impressions} prev={t.prev_impressions} />}
                 spark={kpiSpark(t.trend?.impressions, 'Impressions')} />
            <Kpi label="Avg position" value={formatPos(t.position)} sub={<PosDelta cur={t.position} prev={t.prev_position} />}
                 hint="Impression-weighted; lower is better" spark={kpiSpark(t.trend?.position, 'Average position', true)} />
            <Kpi label="Tracked queries" value={formatCount(counts.tracked)} sub="positions 1–20" />
            <Kpi label="Untapped clicks/mo" value={formatCount(u.top3_clicks || 0)} sub={`${formatUsd(u.top3_usd)} if tracked queries reach #3`}
                 hint={`Model: CTR = ${m.ctr_a}·pos^-${m.ctr_b}; page-two visibility ${m.page2_visibility} per page; position jitter ${m.pos_jitter}; demand from shown days, one-or-two-searcher days set aside; ${formatUsd(m.value_per_visit_usd)} per visit`} />
          </div>
          <p className="muted small" style={{ marginTop: -8 }}>
            {data.window.start} to {data.window.end} (Search Console runs ~3 days behind). Estimated searches and gains come from
            impressions, position and the site&apos;s revenue per visit; treat them as estimates.
          </p>

          <Card title="Trend and changes"
                sub={`The site's ${days <= 28 ? 'daily' : 'weekly'} totals. Flags mark changes and notes from the domain's Notes & follow-ups, so a move can be tied to what was done. Log changes from the domain page or the API.`}>
            <AnnotatedTrend trend={t.trend} start={data.window.start} bucket={days <= 28 ? 1 : 7} entries={timeline} />
          </Card>

          <div style={{ margin: '12px 0 16px' }}>
            <Chips label="Sections" value={section} onChange={setSection}
                   options={SECTIONS.map((s) => ({ ...s, count: counts[s.key] }))} />
          </div>

          {section === 'queries' && (
            <Card title="Top queries">
              <Filters text={text} setText={setText} pos={pos} setPos={setPos} n={n} setN={setN} placeholder="Filter queries" />
              <QueryTable rows={view(data.queries, 'query').slice(0, n)} sort={sort} setSort={setSort} PageCell={PageCell} days={days} />
            </Card>
          )}

          {section === 'pages' && (
            <Card title="Top pages">
              <Filters text={text} setText={setText} pos={pos} setPos={setPos} n={n} setN={setN} placeholder="Filter pages" />
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <SortTh k="clicks" sort={sort} setSort={setSort}>Clicks</SortTh>
                      <SortTh k="impressions" sort={sort} setSort={setSort}>Impr</SortTh>
                      <SortTh k="position" sort={sort} setSort={setSort}>Pos</SortTh>
                      <th className="num">Change</th>
                      <th>Top query</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view(data.pages, 'page').slice(0, n).map((p) => (
                      <tr key={p.page}>
                        <PageCell url={p.page} />
                        <TrendCell values={p.trend?.clicks} metric="Clicks" days={days}>
                          {formatCount(p.clicks)} <PctDelta cur={p.clicks} prev={p.prev_clicks} />
                        </TrendCell>
                        <TrendCell values={p.trend?.impressions} metric="Impressions" days={days}>{formatCount(p.impressions)}</TrendCell>
                        <TrendCell values={p.trend?.position} metric="Position" days={days} invert>{formatPos(p.position)}</TrendCell>
                        <td className="num"><PosDelta cur={p.position} prev={p.prev_position} /></td>
                        <td className="small">{p.top_query}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {section === 'tracked' && (
            <Card title="Tracked queries (positions 1–20)"
                  sub={`Every query at an average position of 20 or better with at least 30 impressions. The trend is the weekly average position over 16 weeks (up = better; dashed line = bottom of page one).`}>
              <Filters text={text} setText={setText} pos={pos} setPos={setPos} placeholder="Filter queries" />
              <QueryTable rows={view(data.tracked, 'query')} sort={sort} setSort={setSort} PageCell={PageCell} days={days} trend />
            </Card>
          )}

          {section === 'opportunities' && (
            <Card title="Opportunities: just below the top"
                  sub="Queries at positions 8–20, ranked by the clicks a move to #3 would add. Work the page that ranks: title and H2 wording, a section that answers the query, internal links with the query as anchor text.">
              <ValueTable rows={data.opportunities} PageCell={PageCell} days={days} />
            </Card>
          )}

          {section === 'lowctr' && (
            <Card title="Low CTR on page one"
                  sub="Top-5 queries getting under half the clicks their position usually gets: the snippet (title, meta description) isn't winning the click.">
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Query</th><th className="num">Pos</th><th className="num">Impr</th><th className="num">CTR</th><th className="num">Expected</th><th>Page</th></tr>
                  </thead>
                  <tbody>
                    {data.low_ctr.map((q) => (
                      <tr key={q.query}>
                        <td>{q.query}</td>
                        <TrendCell values={q.trend?.position} metric="Position" days={days} invert>{formatPos(q.position)}</TrendCell>
                        <TrendCell values={q.trend?.impressions} metric="Impressions" days={days}>{formatCount(q.impressions)}</TrendCell>
                        <td className="num">{((q.clicks / q.impressions) * 100).toFixed(1)}%</td>
                        <td className="num">{(q.expected_ctr * 100).toFixed(1)}%</td>
                        <PageCell url={q.top_page} />
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!data.low_ctr.length && <p className="muted small">None: page-one snippets are earning their expected clicks.</p>}
              </div>
            </Card>
          )}

          {section === 'cannibal' && (
            <Card title="Cannibalization"
                  sub="Queries where two or more of our pages each take a real share of impressions (90 days). Switches = how often Google changed which page it showed first.">
              {cannibal.loading || !cannibal.data ? <Spinner label="Finding split queries…" /> : cannibal.data.error ? (
                <Alert type="error">Could not load cannibalization.</Alert>
              ) : !cannibal.data.queries.length ? <p className="muted">No split queries: each query has one clear page.</p> : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Query</th><th className="num">Impr</th><th>Pages (share · position)</th><th className="num">Switches</th><th>Suggestion</th></tr></thead>
                    <tbody>
                      {cannibal.data.queries.map((c) => (
                        <tr key={c.query}>
                          <td>{c.query}</td>
                          <td className="num">{formatCount(c.impressions)}</td>
                          <td className="small">
                            {c.pages.map((p) => (
                              <div key={p.page}>
                                <a href={p.page} target="_blank" rel="noopener noreferrer" className="mono">{pathOf(p.page)}</a>{' '}
                                <span className="muted">{Math.round(p.share * 100)}% · {formatPos(p.position)}</span>
                                {p.page === c.suggested_target && <> <Badge state="success">keep</Badge></>}
                              </div>
                            ))}
                          </td>
                          <td className="num">{c.top_page_switches}</td>
                          <td className="small" style={{ maxWidth: 360 }}>{c.suggestion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {section === 'diagnosis' && (
            <Card title="Page diagnosis"
                  sub="For each top page: how focused its queries are (share of impressions in its main topic) and the words searchers use that the page doesn't. Fetches the live pages, so the first load takes a moment.">
              {diagnosis.loading || !diagnosis.data ? <Spinner label="Reading pages…" /> : diagnosis.data.error ? (
                <Alert type="error">Could not load page diagnosis.</Alert>
              ) : !diagnosis.data.pages.length ? <p className="muted">No page has enough impressions (50 in 90 days) to judge yet.</p> : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Page</th><th className="num">Impr</th><th className="num">Focus</th><th>Topics</th><th>Main query: missing from title</th><th>Searchers&apos; words it lacks</th></tr></thead>
                    <tbody>
                      {diagnosis.data.pages.map((p) => (
                        <tr key={p.page}>
                          <PageCell url={p.page} />
                          <td className="num">{formatCount(p.impressions)}</td>
                          <td className="num">{Math.round(p.focus * 100)}%{p.flag && <> <Badge state="warn">{p.flag}</Badge></>}</td>
                          <td className="small">{p.topics.slice(0, 3).map((tp) => <div key={tp.topic}>{tp.topic} <span className="muted">{Math.round(tp.share * 100)}%</span></div>)}</td>
                          <td className="small"><strong>{p.main_query}</strong>{p.title_missing.length ? <div className="muted">title lacks: {p.title_missing.join(', ')}</div> : <div className="muted">all in title</div>}</td>
                          <td className="small">{p.missing_words.length ? p.missing_words.map((w) => (
                            <div key={w.word}>{w.word} <span className="muted">{Math.round(w.share * 100)}% · {w.where}</span></div>
                          )) : <Dash />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {section === 'topics' && (
            <TopicsSection state={topics} selected={selCluster} setSelected={setSelCluster} PageCell={PageCell} />
          )}
        </>
      ) : null}
    </div>
  );
};

const QueryTable = ({ rows, sort, setSort, PageCell, days, trend = false }) => (
  <div className="table-wrap">
    <table className="data-table">
      <thead>
        <tr>
          <SortTh k="query" sort={sort} setSort={setSort} num={false}>Query</SortTh>
          <SortTh k="clicks" sort={sort} setSort={setSort}>Clicks</SortTh>
          <SortTh k="impressions" sort={sort} setSort={setSort}>Impr</SortTh>
          <SortTh k="position" sort={sort} setSort={setSort}>Pos</SortTh>
          <th className="num">Change</th>
          {trend && <th style={{ minWidth: 130 }}>Weekly position</th>}
          <SortTh k="days_shown" sort={sort} setSort={setSort} title="Days in the window with at least one impression">Days</SortTh>
          <SortTh k="est_monthly_searches" sort={sort} setSort={setSort} title="Estimated monthly searches if shown on every search, from the days it was shown on pages one and two">Searches/mo</SortTh>
          <th>Page</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((q) => (
          <tr key={q.query}>
            <td>{q.query}</td>
            <TrendCell values={q.trend?.clicks} metric="Clicks" days={days}>{formatCount(q.clicks)}</TrendCell>
            <TrendCell values={q.trend?.impressions} metric="Impressions" days={days}>{formatCount(q.impressions)}</TrendCell>
            <TrendCell values={q.trend?.position} metric="Position" days={days} invert>{formatPos(q.position)}</TrendCell>
            <td className="num"><PosDelta cur={q.position} prev={q.prev_position} /></td>
            {trend && <td><PositionTrend weekly={q.weekly_position} /></td>}
            <td className="num">{q.days_shown != null ? q.days_shown : <Dash />}</td>
            <td className="num">{q.est_monthly_searches != null ? formatCount(q.est_monthly_searches) : <Dash />}</td>
            <PageCell url={q.top_page} />
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <p className="muted small">No queries match.</p>}
  </div>
);

const ValueTable = ({ rows, PageCell, showSite = false, days = 28 }) => (
  <div className="table-wrap">
    <table className="data-table">
      <thead>
        <tr>
          {showSite && <th>Site</th>}
          <th>Query</th>
          <th className="num">Pos</th>
          <th className="num">Impr</th>
          <th className="num" title="Estimated monthly searches">Searches/mo</th>
          <th className="num" title="Extra clicks a month at position 10">+clicks @10</th>
          <th className="num" title="Extra clicks a month at position 3">+clicks @3</th>
          <th className="num">+$/mo @3</th>
          <th>Page</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((q) => (
          <tr key={`${q.domain_id || ''}|${q.query}`}>
            {showSite && <td><Link to={`/domain/${q.domain_id}/rankings?tab=opportunities`}>{q.domain_id.replace(/_/g, '.')}</Link></td>}
            <td>{q.query}</td>
            <TrendCell values={q.trend?.position} metric="Position" days={days} invert>{formatPos(q.position)}</TrendCell>
            <TrendCell values={q.trend?.impressions} metric="Impressions" days={days}>{formatCount(q.impressions)}</TrendCell>
            <td className="num">{q.est_monthly_searches != null ? formatCount(q.est_monthly_searches) : <Dash />}</td>
            <td className="num">{formatCount(Math.round(q.gain_page1_clicks || 0))}</td>
            <td className="num"><strong>{formatCount(Math.round(q.gain_top3_clicks || 0))}</strong></td>
            <td className="num">{formatUsd(q.gain_top3_usd)}</td>
            {PageCell ? <PageCell url={q.top_page} /> : <td className="small mono">{pathOf(q.top_page)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <p className="muted small">No queries at positions 8–20 with enough impressions yet.</p>}
  </div>
);

const TopicsSection = ({ state, selected, setSelected, PageCell }) => {
  const d = state.data;
  const [showAll, setShowAll] = useState(false);
  const flagged = useMemo(() => (d && d.pages ? d.pages.filter((p) => p.flag) : []), [d]);
  if (state.loading || !d) return <Card title="Topics"><Spinner label="Mapping topics (the first run for a site embeds its pages, about a minute)…" /></Card>;
  if (d.error === true) return <Card title="Topics"><Alert type="error">Could not build the topic map.</Alert></Card>;
  if (d.error) return <Card title="Topics"><p className="muted">{d.error}</p></Card>;
  return (
    <>
      <div className="kpi-grid">
        <Kpi label="On-topic" value={`${d.on_topic}`} sub="how tightly pages cluster (0–100)"
             hint="Mean cosine similarity of each page to the site's content centroid" />
        <Kpi label="Content vs demand gap" value={d.content_vs_demand_gap ?? '—'}
             sub="0 = Google shows us for what we write" hint="1 − cos(content centroid, impression-weighted query centroid)" />
        <Kpi label="Pages / queries mapped" value={`${d.pages_analyzed} / ${d.queries_analyzed}`} sub={`${d.days_with_data} days of data`} />
      </div>
      {d.early_data && (
        <Alert type="info">Early data: under 60 days or 1,000 impressions, so repurpose and merge advice is held back. Content-side scores are already reliable.</Alert>
      )}
      <Card title="Topic map" sub="Pages and queries placed by meaning. Where query dots gather with no squares nearby, Google is showing us for something we barely write about.">
        <TopicMap map={d.map} selected={selected} onSelect={(c) => setSelected(c === selected ? null : c)} />
      </Card>
      <Card title="Topics and what to do">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Topic (top queries)</th><th>Action</th><th className="num">Content</th><th className="num">Impressions</th><th className="num">Clicks</th><th className="num">Pos</th><th className="num">Easy +clicks</th><th className="num">+$/mo</th></tr>
            </thead>
            <tbody>
              {d.clusters.map((c) => (
                <tr key={c.cluster} onClick={() => setSelected(c.cluster === selected ? null : c.cluster)}
                    style={{ cursor: 'pointer', background: c.cluster === selected ? '#eff6ff' : undefined }}
                    aria-selected={c.cluster === selected}>
                  <td style={{ maxWidth: 380 }}>
                    <div>{c.label}</div>
                    <div className="muted small">{c.pages} pages · {c.queries} queries</div>
                  </td>
                  <td><Badge state={(ACTIONS[c.action] || {}).badge} title={(ACTIONS[c.action] || {}).hint}>{c.action}</Badge></td>
                  <td className="num">{Math.round(c.content_share * 100)}%</td>
                  <td className="num">{Math.round(c.impression_share * 100)}% <span className="muted small">({formatCount(c.impressions)})</span></td>
                  <td className="num">{formatCount(c.clicks)}</td>
                  <td className="num">{formatPos(c.position)}</td>
                  <td className="num">{c.easy_gain_clicks ? formatCount(Math.round(c.easy_gain_clicks)) : <Dash />}</td>
                  <td className="num">{c.easy_gain_usd ? formatUsd(c.easy_gain_usd) : <Dash />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small">
          {Object.entries(ACTIONS).filter(([k]) => d.clusters.some((c) => c.action === k)).map(([k, v]) => (
            <span key={k} style={{ display: 'block' }}><strong>{k}</strong>: {v.hint}</span>
          ))}
          <span style={{ display: 'block', marginTop: 6 }}>
            Pages join the topic their own Google queries put them in; pages with no impressions follow the ranking page they
            most resemble. &ldquo;No matching demand&rdquo; groups older pages that resemble no ranking page. New pages (under 90 days, or
            not in the DAM) are placed by meaning alone and never suggested for merging.
          </span>
        </p>
      </Card>
      {flagged.length > 0 && (
        <Card title={`Merge or 301 candidates (${flagged.length})`}
              sub="Pages over 90 days old with no impressions in 90 days, in topics Google doesn't show us for. Merge the useful parts into the strongest page on the topic and redirect, or retarget; don't just delete. Pattern and reference pages may be worth keeping for readers anyway.">
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Page</th><th className="num">Age</th></tr></thead>
              <tbody>{(showAll ? flagged : flagged.slice(0, 25)).map((p) => (
                <tr key={p.page}>
                  <PageCell url={p.page} />
                  <td className="num">{p.age_days != null ? (p.age_days >= 730 ? `${Math.round(p.age_days / 365)} yr` : `${Math.round(p.age_days / 30)} mo`) : <Dash />}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {flagged.length > 25 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show fewer' : `Show all ${flagged.length}`}
            </button>
          )}
        </Card>
      )}
    </>
  );
};

export { ValueTable };
export default DomainRankings;
